function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow?.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Run initially after DOM is ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        this.updateFormVisibility();
        this.observeAppstleWidget();
      });
    } else {
      this.updateFormVisibility();
      this.observeAppstleWidget();
    }

    // Listen for variant change
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);
      this.updateFormVisibility();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  observeAppstleWidget() {
    // Only run observer once
    if (this._appstleObserver) return;

    const observer = new MutationObserver(() => {
      const widget = document.querySelector(".appstle_sub_widget");
      if (widget) {
        this.updateFormVisibility();
        observer.disconnect(); // stop observing once widget is found
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    this._appstleObserver = observer;
  }

  updateFormVisibility() {
    let selectedValue = null;

    // Get select value
    const selectElement = this.querySelector("select");
    if (selectElement && selectElement.selectedIndex >= 0) {
      selectedValue = selectElement.options[selectElement.selectedIndex].value;
    }

    // Or get radio value
    const selectedRadio = this.querySelector('input[type="radio"]:checked');
    if (selectedRadio) {
      selectedValue = selectedRadio.value;
    }

    const isFreeGloves = selectedValue === "FREE GLOVES";

    const toggle = (selector, show) => {
      const el = document.querySelector(selector);
      if (el) el.style.display = show ? "block" : "none";
    };

    toggle(".product-form", !isFreeGloves);
    toggle("#free_options", isFreeGloves);
    toggle(".product-form__quantity", !isFreeGloves);
    toggle(".appstle_sub_widget", !isFreeGloves);
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options).forEach((option) =>
        option.removeAttribute("selected")
      );
      target.selectedOptions[0].setAttribute("selected", "selected");
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(".product-form__input")
        ?.querySelector("[data-selected-value]");
      if (selectedSwatchValue) {
        selectedSwatchValue.textContent = value;
      }
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], input[type='radio']:checked")
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

document.addEventListener("DOMContentLoaded", function () {

    const observer = new MutationObserver(() => {
        let termsLink = document.querySelector(".sc-1absc7o-1 .text-zone-element p a");

        if (termsLink && !termsLink.dataset.replaced) {
            observer.disconnect(); // Stop observing once found

            const fileUrl = termsLink.href;

            if (!fileUrl) {
                console.error("Error: File URL is missing!");
                return;
            }

            // Create a new span element to replace the link
            let customLink = document.createElement("span");
            customLink.textContent = termsLink.textContent; // Keep the text
            customLink.style.cssText = "cursor: pointer;";
            customLink.dataset.replaced = "true"; // Mark as replaced

            // Force replace the original link
            termsLink.parentNode.replaceChild(customLink, termsLink);

            // Reconnect observer to detect any Shopify/Yotpo re-injection
            observer.observe(document.body, { childList: true, subtree: true });

            // Attach event to the new span (not an <a> tag, so Shopify won't interfere)
            customLink.addEventListener("click", function (event) {
                event.preventDefault();

                // If modal doesn't exist, create it
                if (!document.getElementById("termsModal")) {
                    createTermsModal();
                }

                // Show the modal
                const modal = document.getElementById("termsModal");
                modal.style.display = "flex";

                // Fetch and display the text file content
                fetch(fileUrl)
                    .then(response => {
                        if (!response.ok) throw new Error("Failed to load terms.");
                        return response.text();
                    })
                    .then(data => {
                        document.getElementById("termsText").textContent = data;
                    })
                    .catch(error => {
                        console.error("Fetch error:", error);
                        document.getElementById("termsText").textContent = "Error loading terms.";
                    });

                return false; // Fully stop any default link behavior
            });
        }
    });

    // Start observing the document for dynamically loaded elements
    observer.observe(document.body, { childList: true, subtree: true });

    function createTermsModal() {
        const svg_close = '<svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M0 3C0 1.34315 1.34315 0 3 0H21C22.6569 0 24 1.34315 24 3V21C24 22.6569 22.6569 24 21 24H3C1.34315 24 0 22.6569 0 21V3Z" fill="#2F2F2FFF" transform="translate(4, 4)"></path><path d="M18 6L6 18" stroke="#FFFFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" transform="translate(4, 4)"></path><path d="M6 6L18 18" stroke="#FFFFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" transform="translate(4, 4)"></path></svg>';

        // Modal container
        const modal = document.createElement("div");
        modal.id = "termsModal";
        modal.style.cssText = `
            display: none; position: fixed; z-index: 2147483648; left: 0; top: 0;
            width: 100%; height: 100%; background-color: rgba(0,0,0,0.5);
            justify-content: center; align-items: center;
        `;

        // Modal content
        const modalContent = document.createElement("div");
        modalContent.classList.add("special-container-link");
        modalContent.style.cssText = `
            background: rgb(47, 47, 47); padding: 20px; width: 80%; max-width: 600px;
            border-radius: 8px; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
            overflow-y: auto; max-height: 80vh; position:relative;
        `;

        // Close button
        const closeButton = document.createElement("span");
        closeButton.innerHTML = svg_close;
        closeButton.style.cssText = `cursor: pointer; position: absolute; right: 20px;`;
        modalContent.classList.add("special-close-link");
        closeButton.addEventListener("click", function () {
            modal.style.display = "none";
        });

        // Text content area
        const termsText = document.createElement("pre");
        termsText.id = "termsText";
        termsText.style.cssText = 'white-space: break-spaces;';
        termsText.textContent = "Loading...";

        // Append everything
        modalContent.appendChild(closeButton);
        modalContent.appendChild(termsText);
        modal.appendChild(modalContent);
        document.body.appendChild(modal);

        // Close modal when clicking outside
        modal.addEventListener("click", function (event) {
            if (event.target === modal) {
                modal.style.display = "none";
            }
        });
    }
});

document.addEventListener("DOMContentLoaded", function () {
  const buttons = document.querySelectorAll('button.slider-counter__link');

  buttons.forEach(button => {
    button.addEventListener('click', function () {
      if (button.classList.contains('slider-counter__link--active')) {
        const targetUrl = button.getAttribute('aria-url');
        if (targetUrl) {
          window.location.href = targetUrl;
        }
      }
    });
  });
});

function handlePriceIdToggle() {
  const desktopPrice = document.querySelector('.price_container.desktop_price');
  const mobilePrice = document.querySelector('.price_container.mobile_price');

  // Store original IDs if not already stored
  if (desktopPrice && !desktopPrice.dataset.originalId) {
    desktopPrice.dataset.originalId = desktopPrice.id;
  }
  if (mobilePrice && !mobilePrice.dataset.originalId) {
    mobilePrice.dataset.originalId = mobilePrice.id;
  }

  if (window.innerWidth <= 749) {
    // On mobile: remove desktop ID, restore mobile ID
    if (desktopPrice) desktopPrice.removeAttribute('id');
    if (mobilePrice && mobilePrice.dataset.originalId) {
      mobilePrice.id = mobilePrice.dataset.originalId;
    }
  } else {
    // On desktop: remove mobile ID, restore desktop ID
    if (mobilePrice) mobilePrice.removeAttribute('id');
    if (desktopPrice && desktopPrice.dataset.originalId) {
      desktopPrice.id = desktopPrice.dataset.originalId;
    }
  }
}

document.addEventListener('DOMContentLoaded', handlePriceIdToggle);
window.addEventListener('resize', handlePriceIdToggle);

document.addEventListener("DOMContentLoaded", function () {
    function geturlLang() {
        return window.location.pathname.includes("/es/") ? "es" : "en";
    }

    function toggleLanguageText() {
        const spanishEl = document.querySelector(
            '#build_a_box_container #spanish'
        );

        const englishEl = document.querySelector(
            '#build_a_box_container #english'
        );

        if (spanishEl && englishEl) {
            if (geturlLang() === 'en') {
                spanishEl.style.display = 'none';
                englishEl.style.display = 'block';
            } else {
                spanishEl.style.display = 'block';
                englishEl.style.display = 'none';
            }
            return true;
        }
        return false;
    }

    if (toggleLanguageText()) return;


    const observer = new MutationObserver(() => {
        if (toggleLanguageText()) {
            observer.disconnect();
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
});

class VideoSlider extends SliderComponent {
  constructor() {
    super();

    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;
    this.unmuteAll = false;
    this.autoplayTimeout = null;
    this.autoplayButtonIsSetToPlay = true;
    this.lastScheduledPage = null;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow?.length > 0) this.currentPage = 1;

    this.initializeMuteButtons();

    if (!this.sliderControlWrapper) return;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll('.slider-counter__link')
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener('click', this.linkToSlide.bind(this))
    );

    this.initArrowButtons();
    this.setSlideVisibility();

    if (this.slider.getAttribute('data-autoplay') === 'true') {
      this.setAutoPlay();
    }
  }

  getVisibleVideo(wrapper) {
    const videos = wrapper.querySelectorAll('video');
    return Array.from(videos).find(video => {
      const style = window.getComputedStyle(video);
      return style.display !== 'none' && style.visibility !== 'hidden' && video.offsetParent !== null;
    }) || null;
  }

  initArrowButtons() {
    const prevButton = this.sliderControlWrapper.querySelector('.slider-button--prev');
    const nextButton = this.sliderControlWrapper.querySelector('.slider-button--next');

    if (prevButton) {
      prevButton.addEventListener('click', () => {
        this.clearScheduledAutoplay();
        const prevIndex =
          this.currentPage === 1 ? this.sliderItems.length : this.currentPage - 1;
        const scrollPos = this.sliderFirstItemNode.clientWidth * (prevIndex - 1);
        this.currentPage = prevIndex;
        this.setSlidePosition(scrollPos);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        this.clearScheduledAutoplay();
        const nextIndex =
          this.currentPage === this.sliderItems.length ? 1 : this.currentPage + 1;
        const scrollPos = this.sliderFirstItemNode.clientWidth * (nextIndex - 1);
        this.currentPage = nextIndex;
        this.setSlidePosition(scrollPos);
      });
    }
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({ left: position });
      requestAnimationFrame(() => {
        this.setSlideVisibility();
      });
    }, 0);
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const isActive = index === this.currentPage - 1;
      const videoWrappers = item.querySelectorAll('.video-wrapper');

      item.setAttribute('aria-hidden', String(!isActive));
      item.tabIndex = isActive ? 0 : -1;
      item.querySelectorAll('a').forEach((el) => el.tabIndex = isActive ? 0 : -1);

      videoWrappers.forEach(wrapper => {
        const video = this.getVisibleVideo(wrapper);
        const button = wrapper.querySelector('.video-mute-toggle');
        const iconMute = button?.querySelector('.icon-mute');
        const iconUnmute = button?.querySelector('.icon-unmute');

        if (!video) return;

        video.pause();
        video.currentTime = 0;

        if (isActive) {
          if (this.unmuteAll) video.muted = false;

          setTimeout(() => {
            video.play().catch(err => console.warn('🔇 Autoplay blocked:', err));
          }, 0);

          // Set onended handler only once per video
          if (!video.dataset.autoplayHandlerSet) {
            video.onended = () => {
              if (this.autoplayButtonIsSetToPlay === true) {
                this.goToNextSlide();
              }
            };
            video.dataset.autoplayHandlerSet = 'true';
          }

          if (button && iconMute && iconUnmute) {
            if (video.muted) {
              iconMute.style.display = 'inline';
              iconUnmute.style.display = 'none';
              button.setAttribute('aria-label', 'Unmute video');
            } else {
              iconMute.style.display = 'none';
              iconUnmute.style.display = 'inline';
              button.setAttribute('aria-label', 'Mute video');
            }
          }
        }
      });
    });

    // Only reschedule if slide actually changed and autoplay is enabled
    if (this.autoplayButtonIsSetToPlay === true &&
        this.lastScheduledPage !== this.currentPage) {
      this.lastScheduledPage = this.currentPage;
      this.scheduleNext();
    }
  }

  goToNextSlide() {
    const nextIndex = this.currentPage === this.sliderItems.length ? 1 : this.currentPage + 1;
    const scrollPos = this.sliderFirstItemNode.clientWidth * (nextIndex - 1);
    this.currentPage = nextIndex;
    this.setSlidePosition(scrollPos);
  }

  linkToSlide(event) {
    event.preventDefault();
    this.clearScheduledAutoplay();
    const targetIndex = this.sliderControlLinksArray.indexOf(event.currentTarget) + 1;
    const scrollPos = this.sliderFirstItemNode.clientWidth * (targetIndex - 1);
    this.currentPage = targetIndex;
    this.setSlidePosition(scrollPos);
  }

  setAutoPlay() {
    this.autoplayButtonIsSetToPlay = true;
    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
    }
    this.play();
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    this.autoplayButtonIsSetToPlay = true;
    this.clearScheduledAutoplay();
    this.scheduleNext();
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    this.autoplayButtonIsSetToPlay = false;
    this.clearScheduledAutoplay();
  }

  autoPlayToggle() {
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
    this.togglePlayButtonState(!this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.play() : this.pause();
  }

  togglePlayButtonState(paused) {
    if (!this.sliderAutoplayButton) return;
    this.sliderAutoplayButton.classList.toggle('slideshow__autoplay--paused', paused);
    const label = paused
      ? window.accessibilityStrings.playSlideshow
      : window.accessibilityStrings.pauseSlideshow;
    this.sliderAutoplayButton.setAttribute('aria-label', label);
  }

  autoRotateSlides() {
    const nextIndex = this.currentPage === this.sliderItems.length ? 1 : this.currentPage + 1;
    const scrollPos = this.sliderFirstItemNode.clientWidth * (nextIndex - 1);
    this.currentPage = nextIndex;
    this.setSlidePosition(scrollPos);
  }

  getPerSlideDuration(slide) {
    if (!slide) return 5000;
    const imageMedia = slide.querySelector('[data-slide-type="image"][data-duration]');
    if (imageMedia) {
      const d = parseInt(imageMedia.dataset.duration || '', 10);
      if (!isNaN(d) && d > 0) return d * 1000;
    }
    return 5000;
  }

  scheduleNext() {
    this.clearScheduledAutoplay();

    if (!this.slider || !this.sliderItems || this.sliderItems.length === 0) return;

    const currentSlide = this.sliderItems[this.currentPage - 1];
    if (!currentSlide) return;

    // If the slide contains any <video> element, don't schedule a timeout.
    // Video slides advance via video.onended handlers set in setSlideVisibility().
    const videos = Array.from(currentSlide.querySelectorAll('video'));
    if (videos.length > 0) {
      return;
    }

    // For images (no video elements), use per-slide duration from data-duration
    const delay = this.getPerSlideDuration(currentSlide);
    this.autoplayTimeout = setTimeout(() => {
      this.goToNextSlide();
      setTimeout(() => this.scheduleNext(), 60);
    }, delay);
  }

  clearScheduledAutoplay() {
    if (this.autoplayTimeout) {
      clearTimeout(this.autoplayTimeout);
      this.autoplayTimeout = null;
    }
  }

  getCurrentAutoplaySpeed() {
    const currentSlide = this.sliderItems[this.currentPage - 1];
    const speed = parseInt(currentSlide?.dataset?.speed || 5);
    return speed * 1000;
  }

  update() {
    super.update();
    if (this.enableSliderLooping) {
      this.prevButton?.removeAttribute('disabled');
      this.nextButton?.removeAttribute('disabled');
    }

    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });

    const activeButton = this.sliderControlButtons[this.currentPage - 1];
    if (activeButton) {
      activeButton.classList.add('slider-counter__link--active');
      activeButton.setAttribute('aria-current', true);
    }
  }

  initializeMuteButtons() {
    const wrappers = this.querySelectorAll('.video-wrapper');

    wrappers.forEach((wrapper) => {
      const button = wrapper.querySelector('.video-mute-toggle');
      const video = this.getVisibleVideo(wrapper);
      if (!video || !button) return;

      const iconMute = button.querySelector('.icon-mute');
      const iconUnmute = button.querySelector('.icon-unmute');

      const updateIcons = () => {
        if (video.muted) {
          iconMute.style.display = 'inline';
          iconUnmute.style.display = 'none';
          button.setAttribute('aria-label', 'Unmute video');
          button.classList.add('video-mute-toggle--muted');
        } else {
          iconMute.style.display = 'none';
          iconUnmute.style.display = 'inline';
          button.setAttribute('aria-label', 'Mute video');
          button.classList.remove('video-mute-toggle--muted');
        }
      };

      updateIcons();

      button.addEventListener('click', () => {
        const wasMuted = video.muted;
        video.muted = !video.muted;
        updateIcons();

        if (!video.muted && wasMuted) {
          this.unmuteAll = true;
          video.currentTime = 0;
          setTimeout(() => video.play().catch(console.warn), 0);
        }

        wrappers.forEach((otherWrapper) => {
          const otherVideo = this.getVisibleVideo(otherWrapper);
          const otherButton = otherWrapper.querySelector('.video-mute-toggle');
          const otherIconMute = otherButton?.querySelector('.icon-mute');
          const otherIconUnmute = otherButton?.querySelector('.icon-unmute');

          if (otherWrapper !== wrapper && otherVideo) {
            otherVideo.pause();
            otherVideo.muted = true;
            otherVideo.currentTime = 0;
            if (otherIconMute && otherIconUnmute) {
              otherIconMute.style.display = 'inline';
              otherIconUnmute.style.display = 'none';
              otherButton.setAttribute('aria-label', 'Unmute video');
            }
          }
        });
      });
    });
  }
}

customElements.define('video-slider', VideoSlider);

const devMode = window.BIZZI_CONFIG?.devMode;
const api_proxy = document.getElementById("apicall")?.value || "bizzi-proxy";
const api_root = devMode
  ? window.BIZZI_CONFIG?.apiBaseUrl
  : "https://bizzisolutions.com";
const BIZZI_IMPERSONATED_CUSTOMER_KEY = "impersonated_customer_ID";
const BIZZI_ORIGINAL_CUSTOMER_KEY = "original_customer_ID";
const BIZZI_IMPERSONATED_CUSTOMER_QUERY_KEY = "impersonated_customer_id";

function getBizziStorageValue(storageName, key) {
  try {
    return String(window[storageName]?.getItem(key) || "").trim();
  } catch (_error) {
    return "";
  }
}

function removeBizziStorageValue(storageName, key) {
  try {
    window[storageName]?.removeItem(key);
  } catch (_error) {}
}

function getBizziCustomerInput() {
  return document.getElementById("customer_ID");
}

function getBizziImpersonatedCustomerId() {
  return getBizziStorageValue("sessionStorage", BIZZI_IMPERSONATED_CUSTOMER_KEY);
}

function getBizziShopifyCustomerId() {
  const customerInput = getBizziCustomerInput();
  const savedShopifyId = customerInput?.dataset?.shopifyCustomerId;
  return String(savedShopifyId || customerInput?.value || "").trim();
}

function getBizziEffectiveCustomerId() {
  return getBizziImpersonatedCustomerId() || getBizziShopifyCustomerId();
}

function appendBizziQueryParam(path, key, value) {
  const rawPath = String(path || "");
  const rawValue = String(value || "").trim();

  if (!rawPath || !rawValue) return rawPath;

  const hashIndex = rawPath.indexOf("#");
  const pathBeforeHash = hashIndex === -1 ? rawPath : rawPath.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : rawPath.slice(hashIndex);
  const queryIndex = pathBeforeHash.indexOf("?");

  if (queryIndex !== -1) {
    const search = pathBeforeHash.slice(queryIndex + 1);
    try {
      if (new URLSearchParams(search).has(key)) return rawPath;
    } catch (_error) {
      if (search.includes(`${encodeURIComponent(key)}=`) || search.includes(`${key}=`)) {
        return rawPath;
      }
    }
  }

  const separator = queryIndex === -1 ? "?" : "&";
  return `${pathBeforeHash}${separator}${encodeURIComponent(key)}=${encodeURIComponent(rawValue)}${hash}`;
}

function appendBizziImpersonation(path) {
  return appendBizziQueryParam(
    path,
    BIZZI_IMPERSONATED_CUSTOMER_QUERY_KEY,
    getBizziImpersonatedCustomerId()
  );
}

function syncBizziCustomerInput() {
  const customerInput = getBizziCustomerInput();
  if (!customerInput) return;

  if (!customerInput.dataset.shopifyCustomerId) {
    customerInput.dataset.shopifyCustomerId = customerInput.value || "";
  }

  customerInput.value = getBizziEffectiveCustomerId();
}

window.BizziCustomerContext = {
  impersonatedStorageKey: BIZZI_IMPERSONATED_CUSTOMER_KEY,
  originalStorageKey: BIZZI_ORIGINAL_CUSTOMER_KEY,
  impersonatedQueryKey: BIZZI_IMPERSONATED_CUSTOMER_QUERY_KEY,
  getImpersonatedCustomerId: getBizziImpersonatedCustomerId,
  getShopifyCustomerId: getBizziShopifyCustomerId,
  getEffectiveCustomerId: getBizziEffectiveCustomerId,
  removeImpersonation() {
    removeBizziStorageValue("sessionStorage", BIZZI_IMPERSONATED_CUSTOMER_KEY);
    removeBizziStorageValue("localStorage", BIZZI_IMPERSONATED_CUSTOMER_KEY);
    syncBizziCustomerInput();
  },
  syncCustomerInput: syncBizziCustomerInput
};

window.getBizziCustomerId = getBizziEffectiveCustomerId;

//console.log("BIZZI devMode:", devMode, "API Root:", api_root);

function buildApiUrl(path) {
  const pathWithImpersonation = appendBizziImpersonation(path);

  if (devMode) {
    // local → no proxy
    return `${api_root.replace(/\/$/, "")}${pathWithImpersonation}`;
  } else {
    // production → just prepend /apps/${api_proxy}, no /proxy
    return `${api_root}/apps/${api_proxy}${pathWithImpersonation}`;
  }
}

function showSiteLoadModal(message, state = "success") {
  const modalId = "siteLoadStatusModal";
  let modal = document.getElementById(modalId);

  if (!modal) {
    modal = document.createElement("div");
    modal.id = modalId;
    modal.className = "site-load-status-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-labelledby", "siteLoadStatusModalTitle");
    modal.innerHTML = `
      <div class="site-load-status-modal__dialog">
        <div class="site-load-status-modal__icon" aria-hidden="true"></div>
        <p id="siteLoadStatusModalTitle" class="site-load-status-modal__title"></p>
        <button class="site-load-status-modal__button" type="button">OK</button>
      </div>
    `;

    document.body.appendChild(modal);

    modal.addEventListener("click", (event) => {
      if (
        event.target === modal ||
        event.target.closest(".site-load-status-modal__button")
      ) {
        hideSiteLoadModal();
      }
    });
  }

  modal.dataset.state = state;
  modal.querySelector(".site-load-status-modal__title").textContent = message;
  modal.classList.add("is-visible");
  document.body.classList.add("site-load-status-modal-open");

  window.setTimeout(() => {
    modal.querySelector(".site-load-status-modal__button")?.focus();
  }, 0);
}

function hideSiteLoadModal() {
  const modal = document.getElementById("siteLoadStatusModal");
  if (!modal) return;

  modal.classList.remove("is-visible");
  document.body.classList.remove("site-load-status-modal-open");
}

function showLoadedSuccessfullyModal(message = "Loaded Successfully") {
  showSiteLoadModal(message, "success");
}

function showFailedToLoadModal(message = "Failed To Load") {
  showSiteLoadModal(message, "error");
}

window.showLoadedSuccessfullyModal = showLoadedSuccessfullyModal;
window.showFailedToLoadModal = showFailedToLoadModal;
window.showLoadedSuccessfully = showLoadedSuccessfullyModal;
window.showFailedToLoad = showFailedToLoadModal;
window.showSiteLoadModal = showSiteLoadModal;
window.hideSiteLoadModal = hideSiteLoadModal;

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") {
    hideSiteLoadModal();
  }
});

// ===========================
// Set customer_ID for legacy scripts while preserving the signed Shopify customer ID.
// ===========================
document.addEventListener("DOMContentLoaded", syncBizziCustomerInput);

document.addEventListener("DOMContentLoaded", () => {
  const logoutLink = document.getElementById("logoutLink");
  if (logoutLink) {
    logoutLink.addEventListener("click", () => {
      window.BizziCustomerContext?.removeImpersonation();
      removeBizziStorageValue("sessionStorage", BIZZI_ORIGINAL_CUSTOMER_KEY);
    });
  }
});

async function pushVariableFromSource(agentId, Dynamic_variable, Dynamic_variable_source) {
  const endpoint =
    typeof buildApiUrl === 'function'
      ? buildApiUrl('/retell/push-variable-from-source')
      : '/retell/push-variable-from-source';

  console.log('[retell] pushVariableFromSource request', {
    endpoint,
    agentId,
    Dynamic_variable,
    Dynamic_variable_source,
  });

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      agentId: agentId,
      Dynamic_variable: Dynamic_variable,
      Dynamic_variable_source: Dynamic_variable_source,
    }),
  });

  let payload = {};
  try {
    payload = await response.json();
  } catch (_error) {
    payload = {};
  }

  console.log('[retell] pushVariableFromSource response', {
    endpoint,
    ok: response.ok,
    status: response.status,
    payload,
  });

  if (!response.ok) {
    const error = new Error(
      payload?.error || payload?.message || `Failed to push variable from source (${response.status}).`
    );
    error.status = response.status;
    error.payload = payload;
    console.error('[retell] pushVariableFromSource failed', error);
    throw error;
  }

  return payload;
}

window.pushVariableFromSource = pushVariableFromSource;

// Example:
// window.pushVariableFromSource('agent_123', 'view_calendar_events', 'view_calendar_availability');

document.addEventListener("DOMContentLoaded", () => {
    const superHeaderIcon = document.getElementById("super_icons");
    if (!superHeaderIcon) return;

    const isImpersonating = !!getBizziImpersonatedCustomerId();
    superHeaderIcon.classList.toggle("active", isImpersonating);
  });

document.addEventListener('DOMContentLoaded', async () => {
  // Read customerId first (was referenced before initialization)
  const customerId = getBizziEffectiveCustomerId();
  const bubble = document.querySelector('.bubble-unread');
  const pinnedBubble = document.querySelector('.bubble-pinned');

  let callCountsRequest = null;

  function getCountValue(source, keys) {
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(source || {}, key)) {
        const value = Number(source[key]);
        return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
      }
    }
    return 0;
  }

  function normalizeCallCounts(data) {
    return {
      unread: getCountValue(data, ['unread_calls', 'unreadCalls', 'unread_call_count']),
      pinned: getCountValue(data, ['pinned_calls', 'pinnedCalls', 'pinned_call_count'])
    };
  }

  function updateCallBubble(el, count) {
    if (!el) return;
    el.style.display = count > 0 ? 'flex' : 'none';
    el.textContent = count;
  }

  async function fetchCallCounts() {
    if (!customerId) return { unread: 0, pinned: 0 };

    let path = '/call-counts';
    if (window.BIZZI_CONFIG?.devMode) {
      const devCustomerId = customerId
        || window.BizziCustomerContext?.getShopifyCustomerId?.()
        || document.getElementById('customer_ID')?.value;
      if (devCustomerId) path += '?customer_id=' + encodeURIComponent(devCustomerId);
    }

    const response = await fetch(buildApiUrl(path));
    if (!response.ok) throw new Error(`Unable to load call counts (${response.status}).`);
    return normalizeCallCounts(await response.json());
  }

  async function refreshCallBubbles() {
    if (!callCountsRequest) {
      callCountsRequest = fetchCallCounts()
        .catch((error) => {
          console.error('Error fetching call counts:', error);
          return { unread: 0, pinned: 0 };
        })
        .finally(() => {
          callCountsRequest = null;
        });
    }

    const counts = await callCountsRequest;
    updateCallBubble(bubble, counts.unread);
    updateCallBubble(pinnedBubble, counts.pinned);
    return counts;
  }

  // Updates the unread bubble via the lightweight count endpoint.
  async function update_read_call_bubble() {
    return refreshCallBubbles();
  }

  // Updates the pinned bubble via the lightweight count endpoint.
  async function update_pinned_call_bubble() {
    return refreshCallBubbles();
  }

  async function fetchEttsTicketCount() {
    const ettsBubble = document.querySelector('.bubble-etts');
    if (!ettsBubble || !customerId) return;
    try {
      let path = '/etts/new-ticket-count';
      if (window.BIZZI_CONFIG?.devMode) {
        const devCustomerId = customerId
          || window.BizziCustomerContext?.getShopifyCustomerId?.()
          || document.getElementById('customer_ID')?.value;
        if (devCustomerId) path += '?customer_id=' + encodeURIComponent(devCustomerId);
      }
      const response = await fetch(buildApiUrl(path));
      if (!response.ok) return;
      const data = await response.json();
      const count = data?.count || 0;
      ettsBubble.textContent = count;
      ettsBubble.style.display = count > 0 ? 'flex' : 'none';
    } catch {
      // silently fail — bubble stays hidden
    }
  }

  // 🔹 Expose to global so other files can call
  window.update_read_call_bubble = update_read_call_bubble;
  window.update_pinned_call_bubble = update_pinned_call_bubble;
  window.fetchEttsTicketCount = fetchEttsTicketCount;

  // Call count bubbles use one shared request at start.
  refreshCallBubbles();
  fetchEttsTicketCount();
});




function updateViewportHeight() {
  const viewportHeight = window.visualViewport
    ? window.visualViewport.height
    : window.innerHeight;
  document.documentElement.style.setProperty('--vh', `${viewportHeight}px`);
  return viewportHeight;
}

function setCallListHeaderHeight() {
  const headerWrapper = document.querySelector('.dashboard_call_list_header_container');
  if (!headerWrapper) return;

  // Update --vh each time we calculate
  const viewportHeight = updateViewportHeight();

  // Get header height
  let headerHeight = headerWrapper.getBoundingClientRect().height;

  // On mobile, ensure height doesn't exceed visible viewport
  headerHeight = Math.min(headerHeight, viewportHeight);

  document.documentElement.style.setProperty('--call-list-header-height', `${headerHeight}px`);
}

// Run once on load (after any layout shift)
window.addEventListener('load', () => {
  setTimeout(setCallListHeaderHeight, 300);
});

// Recalculate on resize or orientation change
window.addEventListener('resize', setCallListHeaderHeight);
window.addEventListener('orientationchange', () => {
  setTimeout(setCallListHeaderHeight, 300);
});

document.addEventListener('DOMContentLoaded', function() {
  const customerId = getBizziEffectiveCustomerId();
  const aiSettingsMenuPanel = document.querySelector("[data-ai-settings-menu]");
  const sidebarMenuStatusPanel = document.querySelector("[data-sidebar-menu-status]");
  const sidebarMenuPanels = Array.from(document.querySelectorAll(".link_with_sidebar_links_header > .link_with_sidebar_item.color_panels"));
  const belowMenuContent = document.getElementById("onTrial");
  let isBelowMenuContentReady = !belowMenuContent;
  let isAiSettingsMenuReady = !sidebarMenuStatusPanel;
  let sidebarMenuResizeTimer = null;

  function markBelowMenuContentReady() {
    isBelowMenuContentReady = true;
    document.documentElement.classList.remove("sidebar-below-content-pending");
    document.body.classList.remove("sidebar-below-content-pending");
    document.documentElement.classList.add("sidebar-below-content-ready");
    document.body.classList.add("sidebar-below-content-ready");
    document.dispatchEvent(new CustomEvent("bizzi:below-menu-content-ready"));
    updateAiSettingsLoadingState();
  }

  function waitForBelowMenuContentRender(element) {
    const images = Array.from(element.querySelectorAll("img")).filter((img) => !img.complete);
    const imageLoadPromise = images.length
      ? Promise.all(images.map((img) => new Promise((resolve) => {
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        })))
      : Promise.resolve();

    imageLoadPromise.then(() => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          markBelowMenuContentReady();
          queueSidebarMenuMaxHeight();
        });
      });
    });
  }

  function setSidebarMenuMaxHeight() {
    if (!sidebarMenuPanels.length) return;

    const viewportHeight = updateViewportHeight();
    const bottomGutter = 8;

    sidebarMenuPanels.forEach((panel) => {
      const panelRect = panel.getBoundingClientRect();
      const maxHeight = Math.max(160, Math.floor(viewportHeight - panelRect.top - bottomGutter));

      panel.style.setProperty("--sidebar-menu-max-height", `${maxHeight}px`);
    });
  }

  function queueSidebarMenuMaxHeight() {
    if (!sidebarMenuPanels.length) return;

    window.requestAnimationFrame(() => {
      setSidebarMenuMaxHeight();
      window.setTimeout(setSidebarMenuMaxHeight, 250);
    });
  }

  function bindSidebarMenuSizing() {
    if (!sidebarMenuPanels.length) return;

    queueSidebarMenuMaxHeight();
    window.addEventListener("load", queueSidebarMenuMaxHeight);
    window.addEventListener("resize", () => {
      window.clearTimeout(sidebarMenuResizeTimer);
      sidebarMenuResizeTimer = window.setTimeout(queueSidebarMenuMaxHeight, 80);
    });
    window.addEventListener("orientationchange", () => {
      window.setTimeout(queueSidebarMenuMaxHeight, 300);
    });

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", queueSidebarMenuMaxHeight);
      window.visualViewport.addEventListener("scroll", queueSidebarMenuMaxHeight);
    }
  }

  function setAiSettingsMenuLoading(isLoading) {
    if (!sidebarMenuStatusPanel) return;

    sidebarMenuStatusPanel.classList.toggle("is-menu-loading", Boolean(isLoading));
    const loader = document.getElementById("sidebarMenuStatusLoader");
    if (loader) {
      loader.setAttribute("aria-hidden", isLoading ? "false" : "true");
    }

    queueSidebarMenuMaxHeight();
  }

  function updateAiSettingsLoadingState() {
    const statusText = document.getElementById("sidebarMenuStatusText");
    if (!isAiSettingsMenuReady) {
      if (statusText) statusText.textContent = "Loading Menu ...";
      setAiSettingsMenuLoading(false);
      return;
    }

    if (!isBelowMenuContentReady) {
      if (statusText) statusText.textContent = "Loading agent status";
      setAiSettingsMenuLoading(false);
      return;
    }

    setAiSettingsMenuLoading(false);
  }

  bindSidebarMenuSizing();

  if (customerId) {
    const pageHandle = document.body.dataset.pageHandle;
    const retellInfoPages = [
      "ai-settings",
      "ai-agent-settings",
      "agent-settings",
      "company-profile",
      "notifications",
      "call-transfer",
      "call-transfers",
      "q-a-agent-training",
      "behavior-change-request",
      "calendar-integration",
      "chatgenie-settings",
    ];

    if (aiSettingsMenuPanel || retellInfoPages.includes(pageHandle)) {
      getRetellPhoneNumber(customerId);
    } else {
      window.requestAnimationFrame(() => {
        isAiSettingsMenuReady = true;
        updateAiSettingsLoadingState();
      });
    }
    checkTrial(customerId);
  } else {
    isAiSettingsMenuReady = true;
    markBelowMenuContentReady();
  }

  async function getRetellPhoneNumber(customerId) {
    try {
      // Example: fetch RetellAI info

      if (!customerId) {
        console.warn("No Shopify customer ID available");
        return;
      }

      const res = await fetch(buildApiUrl(`/get-retell-information?customer_id=${customerId}`));
      const data = await res.json();

      if (data.error) {
        console.error("Error fetching RetellAI info:", data.error);
        return;
      }

      const phoneEl = document.getElementById("retell_phonenumber");
      const rawPhoneNumber = String(data.phoneNumber || "");
      const digits = rawPhoneNumber.replace(/\D/g, "");
      const trimmed = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

      if (phoneEl && trimmed.length === 10) {
        const areaCode = trimmed.slice(0, 3);
        const centralOffice = trimmed.slice(3, 6);
        const lineNumber = trimmed.slice(6);

        phoneEl.textContent = `(${areaCode}) ${centralOffice}-${lineNumber}`;
      }
      let customer_service_level = Number(data.serviceLevel) || 0;

      const callTransferItem = document.getElementById("call-transfer-hide");
      if (callTransferItem) {
        callTransferItem.style.display = customer_service_level >= 2 ? "block" : "none";
      }

      const qaAgentTrainingItem = document.getElementById("qa-agent-training-hide");
      if (qaAgentTrainingItem) {
        qaAgentTrainingItem.style.display = customer_service_level >= 2 ? "block" : "none";
      }

      // TODO: Use `data` to populate the page
    } catch (err) {
      console.error("❌ Failed to initialize AI Settings page:", err);
    } finally {
      isAiSettingsMenuReady = true;
      updateAiSettingsLoadingState();
    }
  }

  async function checkTrial(customerId) {
    let trial_title;
    let trial_summary;

    try {
      // ✅ Using buildApiUrl (no duplicate api_proxy)
      const response = await fetch(buildApiUrl('/check-trial'), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ customerId }),
      });

      const data = await response.json();
      const onTrialDiv = document.getElementById("onTrial");
      if (onTrialDiv) {
        const show_whats_left = document.getElementById("show_whats_left");
      const trial_summaryDiv = document.getElementById("trial_summary");
      const trial_titleDiv = document.getElementById("trial_title");
      const trialBadge = document.querySelector('.header__icon--account .notify');
      const setBelowMenuContentLayout = (isVisible) => {
        document.documentElement.classList.toggle("sidebar-has-below-content", isVisible);
        document.body.classList.toggle("sidebar-has-below-content", isVisible);
      };


      const totalMinutes = data.remainingMinutes;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = Number((totalMinutes % 60).toFixed(1));
      const displayMinutes = Number.isInteger(minutes) ? minutes : minutes.toFixed(1);

      if (data.existsInTrialTracking) {
        onTrialDiv.style.display = "block";
        setBelowMenuContentLayout(true);
        if(data.status == 1){
          if (trialBadge) trialBadge.style.display = "block";
          show_whats_left.style.display = "block";
          trial_title = "Your AI Agent is in trial mode!";
          trial_summary = "Claim your agent now to keep your phone number and avoid any service interruptions. Don't miss out — activate your plan before your trial expires.";
          document.getElementById("trial_days").textContent = data.daysLeft;
          document.getElementById("trial_minutes").textContent =
  `${hours > 0 ? hours + " " + (hours === 1 ? "hour " : "hours ") : ""}${displayMinutes} ${displayMinutes === 1 ? "minute" : "minutes"}`;
          trial_titleDiv.style.color = "#73a13d";
        }else if(data.status == 2){
          if (trialBadge) trialBadge.style.display = "block";
          show_whats_left.style.display = "none";
          trial_title = "Your AI Agent trial has ENDED!";
          trial_summary = "Upgrade your agent now to reactivate. Don't miss out — activate your plan before your trial expires.";
          trial_titleDiv.style.color = "#FE8125";
        }else if(data.status == 3){
          if (trialBadge) trialBadge.style.display = "block";
          show_whats_left.style.display = "none";
          trial_title = "Your AI Agent trial is disabled!";
          trial_summary = "Upgrade your agent now to reactivate. Don't miss out — activate your plan before your trial expires.";
          trial_titleDiv.style.color = "#FE8125";
        } else {
          if (trialBadge) trialBadge.style.display = "none";
          onTrialDiv.style.display = "none";
          setBelowMenuContentLayout(false);
        }

        trial_titleDiv.textContent = trial_title;
        trial_summaryDiv.textContent = trial_summary;
        waitForBelowMenuContentRender(onTrialDiv);
      } else {
        if (trialBadge) trialBadge.style.display = "none";
        onTrialDiv.style.display = "none";
        setBelowMenuContentLayout(false);
        markBelowMenuContentReady();
      }
      }

    } catch (error) {
      console.error("Error checking trial:", error);
      markBelowMenuContentReady();
    }
  }

  // Select all Buy Now buttons with the same class
  const buyNowButtons = document.querySelectorAll('.buy-now');

  buyNowButtons.forEach(button => {
    button.addEventListener('click', function(event) {
      event.preventDefault();

      const productId = button.dataset.variantId;      // ← correct
      const sellingPlanId = button.dataset.sellingPlanId;

      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {

          // Check if product already exists in cart
          const exists = cart.items.some(item => item.id == productId);

          if (exists) {
            window.location.href = '/checkout';
            return;
          }

          // Add product if not exists
          return fetch('/cart/add.js', {

            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: productId,
              quantity: 1,
              selling_plan: sellingPlanId

            })
          }).then(() => window.location.href = '/checkout');
        })
        .catch(() => {
          // Fallback if /cart.js fails
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: productId,
              quantity: 1,
              selling_plan: sellingPlanId
            })
          }).then(() => window.location.href = '/checkout');
        });
    });
  });
});
